/**
 * Le jeu d'extensions TipTap — la moitié cliente de l'allowlist du serveur.
 *
 * `teach/shared/richtext.py` décide ce qui entre en base ; ce fichier décide ce
 * que l'éditeur sait produire. Les deux listes existent parce qu'elles sont
 * écrites dans deux langages, et **c'est un test qui les tient d'accord** :
 * `packages/contracts/richtext-sample.json` porte un document qui exerce chaque
 * nœud, chaque marque et chaque attribut ; la suite Python vérifie que le
 * serveur l'accepte **sans rien y changer**, la suite front qu'un vrai éditeur
 * le relit et le ressort identique. Une divergence casse l'une des deux.
 *
 * ## Ce qui est configuré, et pourquoi
 *
 * Quatre réglages ne sont pas cosmétiques — sans eux l'éditeur produit un
 * document que le serveur refuse en 422, et l'auteur perd ce qu'il vient
 * d'écrire :
 *
 * 1. `heading.levels` est borné à 2–4 : le titre de la leçon EST son h1.
 * 2. `Link` perd `class` et `title`. Un attribut `class` posé par un collage
 *    depuis une page extérieure serait exactement la valeur libre que
 *    `_validate_language` refuse côté serveur, et pour la même raison.
 * 3. `OrderedList` perd `type` (le style de numérotation HTML) : rien ne le
 *    rend, et le serveur le refuse.
 * 4. Les cellules de tableau perdent `colwidth` et `align`. `colwidth` est la
 *    largeur qu'un redimensionnement de colonne écrirait — geste que cet
 *    éditeur n'offre pas, et une largeur enregistrée que le rendu élève
 *    ignorerait ferait diverger en silence ce que l'auteur voit de ce que
 *    l'élève voit. `align` est un alignement de cellule que TipTap rend dans un
 *    `style`, et qui ferait exister une **seconde** façon d'aligner ce que
 *    `textAlign` aligne déjà sur le paragraphe de la cellule.
 *
 * `Underline` vient de `@tiptap/extension-underline` et **StarterKit le
 * désactive** : le kit v3 l'embarque déjà, et enregistrer deux fois le même nom
 * d'extension fait diverger le schéma de ProseMirror de celui que l'on croit
 * avoir — TipTap se contente d'un avertissement en console.
 */
import Placeholder from "@tiptap/extension-placeholder";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import type { AnyExtension } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

/** Niveaux de titre acceptés par le serveur. */
export const HEADING_LEVELS = [2, 3, 4] as const;

/** Alignements acceptés par le serveur (`ALLOWED_TEXT_ALIGN`). */
export const TEXT_ALIGNMENTS = ["left", "center", "right", "justify"] as const;
export type TextAlignment = (typeof TEXT_ALIGNMENTS)[number];

/** Nœuds sur lesquels l'alignement a un sens. */
const ALIGNABLE = ["heading", "paragraph"];

/**
 * Attributs retirés aux extensions, **par nom d'extension**.
 *
 * Le serveur les refuse (`ALLOWED_NODES` / `ALLOWED_MARKS`) : les retirer ici
 * rend l'éditeur incapable de les produire, plutôt que de les nettoyer après
 * coup. `class` sur un lien mérite une mention à part — c'est ce qu'un collage
 * depuis une page extérieure apporte, et une classe arbitraire est exactement
 * la valeur libre que le serveur refuse partout ailleurs.
 */
const DROPPED_ATTRIBUTES: Record<string, readonly string[]> = {
  link: ["class", "title"],
  orderedList: ["type"],
  tableCell: ["colwidth", "align"],
  tableHeader: ["colwidth", "align"],
};

/** Le contexte que TipTap lie à `addAttributes` — voir son usage plus bas. */
interface ExtensionContext {
  parent?: () => Record<string, unknown>;
}

/**
 * Une extension privée des attributs que le serveur refuse.
 *
 * Appliqué aux extensions du kit **et** à celles du tableau, qui sont
 * enregistrées à part : sans passage unique, la table `DROPPED_ATTRIBUTES`
 * aurait deux lecteurs, dont un seul saurait qu'une ligne vient d'y être
 * ajoutée.
 */
function withoutDroppedAttributes(extension: AnyExtension): AnyExtension {
  const dropped = DROPPED_ATTRIBUTES[extension.name];
  if (dropped === undefined) {
    return extension;
  }
  return extension.extend({
    // `this` est déclaré explicitement : TipTap type le contexte d'un
    // `.extend()` appelé sur une extension anonyme par sa seule config, qui ne
    // porte pas `parent`. Il est bien là à l'exécution — c'est ainsi qu'on
    // hérite des attributs de l'extension d'origine.
    addAttributes(this: ExtensionContext) {
      const inherited = this.parent?.() ?? {};
      return Object.fromEntries(
        Object.entries(inherited).filter(([name]) => !dropped.includes(name)),
      );
    },
  });
}

export function richTextExtensions(placeholder: string): AnyExtension[] {
  return [
    StarterKit.configure({
      heading: { levels: [...HEADING_LEVELS] },
      // Désactivé ici, ajouté explicitement plus bas : le paquet nommé par
      // docs/02 est `@tiptap/extension-underline`, et le laisser en double
      // enregistrerait deux fois la marque `underline`.
      underline: false,
      link: {
        openOnClick: false,
        // L'allowlist de schémas est **aussi** côté serveur (`ALLOWED_LINK_SCHEMES`) :
        // celle-ci évite de composer un lien que l'enregistrement refusera,
        // celle-là est la garantie.
        protocols: ["http", "https", "mailto"],
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      },
    }).extend({
      addExtensions() {
        // `this.parent` rend les extensions du kit ; on retire les attributs
        // que le serveur refuse plutôt que de les nettoyer après coup, pour que
        // l'éditeur soit **incapable** de les produire.
        return (this.parent?.() ?? []).map(withoutDroppedAttributes);
      },
    }),
    Underline,
    TextAlign.configure({ types: ALIGNABLE }),
    // `resizable` reste au défaut (`false`) : la poignée de redimensionnement
    // écrit un `colwidth` dans le document, que le rendu élève — un tableau
    // sans `colgroup` — n'appliquerait pas. L'auteur croirait avoir figé une
    // largeur que personne d'autre ne voit.
    ...[Table, TableRow, TableHeader, TableCell].map(withoutDroppedAttributes),
    Placeholder.configure({ placeholder }),
  ];
}
